/**
 * Phone normalisation — ONE definition, shared by everything that compares a phone number.
 *
 * Saudi mobile numbers reach us in at least three spellings, because Salla, Zid and Shopify
 * each disagree about which is canonical:
 *
 *   `0501112233`      national, leading zero
 *   `+966501112233`   E.164
 *   `966501112233`    E.164 without the plus
 *
 * All three name the same subscriber. The nine significant digits (`501112233`) are the
 * only part that is stable across them, so that is what any comparison must use.
 *
 * This is not cosmetic. `policy.blockedPhones` is a fraud/abuse control: comparing raw
 * strings means a merchant who blocks `+966501112233` does not block the same person whose
 * order arrives from a different platform as `0501112233` — the control fails OPEN and
 * silently. Every comparison site in this repo must route through this function, on BOTH
 * sides of the comparison.
 *
 * Pure: no I/O, no ambient time, no random. Safe to import from `packages/returns-core`.
 */

/** How many trailing digits identify a subscriber, once the country/trunk prefix is stripped. */
const SIGNIFICANT_DIGITS = 9;

/**
 * The comparable form of a phone number: its last nine digits, non-digits discarded.
 *
 * Deliberately lossy and deliberately NOT a validator — it makes two spellings comparable
 * and nothing else. A short or malformed input returns whatever digits it had, so callers
 * never get a silent `''` match against another malformed value... except for the genuinely
 * empty case, which is why {@link samePhone} refuses to match on an empty key.
 */
export function normalisePhone(phone: string): string {
  return toLatinDigits(phone).replace(/\D/g, "").slice(-SIGNIFICANT_DIGITS);
}

/** Arabic-Indic (٠-٩) and Extended Arabic-Indic (۰-۹) zero code points. */
const ARABIC_DIGIT_BLOCKS = [0x0660, 0x06f0] as const;

/**
 * `٠٥٠…` -> `050…`. An Arabic keyboard produces these, and `\d` does not match them, so
 * without this fold a merchant's blocklist entry or a shopper's lookup would normalise to
 * `''` and never match anyone.
 */
function toLatinDigits(value: string): string {
  return value.replace(/[٠-٩۰-۹]/g, (char) => {
    const code = char.codePointAt(0) ?? 0;
    const base = ARABIC_DIGIT_BLOCKS.find((b) => code >= b && code <= b + 9);
    return base === undefined ? char : String(code - base);
  });
}

/**
 * Do these two spellings name the same subscriber?
 *
 * Two inputs with no digits at all are NOT equal: an empty normalised key would otherwise
 * make `''` match `''` and turn a missing phone into a positive identification.
 */
export function samePhone(a: string, b: string): boolean {
  const left = normalisePhone(a);
  return left.length > 0 && left === normalisePhone(b);
}

/**
 * Is this number on a blocklist, whatever spelling either side happens to use?
 *
 * Normalises BOTH sides — the list entry as the merchant typed it, and the number as the
 * store platform sent it.
 */
export function phoneInList(phone: string, list: readonly string[]): boolean {
  const key = normalisePhone(phone);
  if (key.length === 0) return false;
  return list.some((entry) => normalisePhone(entry) === key);
}
